import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { LuCheck, LuLoaderCircle, LuPencil, LuPlus, LuSave, LuTrash2 } from 'react-icons/lu';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import { INPUT_CLASS } from '../../components/form/FormControls';

const ROW_BUTTON = 'tw-grid tw-h-9 tw-w-9 tw-shrink-0 tw-place-items-center tw-rounded-full tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 disabled:tw-opacity-50';

/**
 * One value list as a card: rows with the value (locked lists open with the
 * pencil), Aktif / Pasif and delete; "Yeni ekle" and "Kaydet" below. Enter
 * on the last row adds a new one (as before).
 */
const ValueListCard = ({ list, icon: Icon, gradient, label, onRemove }) => {
  const {
    config, items, isLoading, isSaving, checkingUid, editing, errors, dirty, counts,
    updateValue, toggleActive, toggleEdit, addItem, save,
  } = list;
  const focusUid = useRef(null);
  const inputRefs = useRef({});

  useEffect(() => {
    if (focusUid.current && inputRefs.current[focusUid.current]) {
      inputRefs.current[focusUid.current].focus();
      focusUid.current = null;
    }
  });

  const add = () => {
    focusUid.current = addItem();
  };

  const handleKeyDown = (event, isLast) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    if (isLast) add();
  };

  const text = (key, vars) => label(`lists.${config.key}.${key}`, vars);
  const busy = isSaving || isLoading;

  return (
    <section aria-labelledby={`list-${config.key}`} className="tw-flex tw-min-w-0 tw-flex-col tw-rounded-[1.5rem] tw-border tw-border-white/80 tw-bg-white/80 tw-p-4 tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_20px_50px_-30px_rgba(15,23,42,0.35)] sm:tw-p-5 dark:tw-border-white/10 dark:tw-bg-[#22262f]/80 dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_20px_50px_-30px_rgba(0,0,0,0.9)]">
      <header className="tw-flex tw-items-start tw-gap-3">
        <GlassIcon gradient={gradient} size="xs">
          <Icon aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
        <div className="tw-min-w-0 tw-flex-1">
          <h3 id={`list-${config.key}`} className="tw-text-base tw-font-semibold tw-leading-tight tw-text-slate-900 dark:tw-text-white">{text('title')}</h3>
          <p className="tw-mt-1 tw-text-xs tw-leading-relaxed tw-text-slate-500 dark:tw-text-slate-400">{text('helper')}</p>
          <div className="tw-mt-2 tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
            <Badge tone="success">{label('card.active', { count: counts.active })}</Badge>
            {counts.passive > 0 && <Badge tone="neutral">{label('card.passive', { count: counts.passive })}</Badge>}
            {dirty && <Badge tone="warning">{label('card.unsaved')}</Badge>}
          </div>
        </div>
      </header>

      <div className="tw-mt-4 tw-flex-1">
        {isLoading ? (
          <div className="tw-space-y-2">
            {[0, 1, 2].map((i) => <div key={i} className="tw-h-10 tw-animate-pulse tw-rounded-xl tw-bg-slate-200/70 dark:tw-bg-white/[0.06]" />)}
          </div>
        ) : (
          <ul className="tw-space-y-2">
            {items.map((item, index) => {
              const locked = config.lockable && !editing[item.uid];
              const isLast = index === items.length - 1;
              const checking = checkingUid === item.uid;
              const error = errors[item.uid];
              const ariaName = `${text('itemName', { index: index + 1 })}${item.isActive ? '' : ` (${label('card.passiveShort')})`}`;
              return (
                <li key={item.uid}>
                  <div className="tw-flex tw-items-center tw-gap-1.5">
                    <input
                      ref={(node) => { inputRefs.current[item.uid] = node; }}
                      type="text"
                      value={item.value}
                      onChange={(event) => updateValue(item.uid, event.target.value)}
                      onKeyDown={(event) => handleKeyDown(event, isLast)}
                      readOnly={locked}
                      placeholder={text('placeholder')}
                      aria-label={ariaName}
                      aria-invalid={error ? true : undefined}
                      className={`${INPUT_CLASS} tw-min-w-0 tw-flex-1 ${locked ? 'tw-cursor-default tw-bg-slate-50/70 dark:tw-bg-white/[0.02]' : ''} ${item.isActive ? '' : 'tw-text-slate-400 tw-line-through dark:tw-text-slate-500'} ${error ? 'tw-ring-2 tw-ring-rose-500/50' : ''}`}
                    />
                    <button
                      type="button"
                      onClick={() => toggleActive(item.uid)}
                      aria-pressed={item.isActive}
                      title={item.isActive ? label('card.makePassive') : label('card.makeActive')}
                      className={`tw-h-9 tw-shrink-0 tw-rounded-full tw-border tw-px-3 tw-text-xs tw-font-semibold tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/40 ${item.isActive
                        ? 'tw-border-emerald-500/30 tw-bg-emerald-500/10 tw-text-emerald-700 dark:tw-text-emerald-300'
                        : 'tw-border-slate-300 tw-bg-slate-500/10 tw-text-slate-600 dark:tw-border-white/15 dark:tw-text-slate-300'}`}
                    >
                      {item.isActive ? label('card.activeShort') : label('card.passiveShort')}
                    </button>
                    {config.lockable && (
                      <button
                        type="button"
                        onClick={() => toggleEdit(item.uid)}
                        title={editing[item.uid] ? label('card.doneEditing') : label('card.edit')}
                        aria-label={editing[item.uid] ? label('card.doneEditing') : label('card.edit')}
                        className={`${ROW_BUTTON} tw-text-blue-600 hover:tw-bg-blue-500/10 focus-visible:tw-ring-blue-500/40 dark:tw-text-blue-300`}
                      >
                        {editing[item.uid] ? <LuCheck aria-hidden="true" className="tw-h-4 tw-w-4" /> : <LuPencil aria-hidden="true" className="tw-h-4 tw-w-4" />}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => onRemove(list, item.uid)}
                      disabled={isSaving || Boolean(checkingUid)}
                      title={label('card.delete')}
                      aria-label={`${ariaName} – ${label('card.delete')}`}
                      className={`${ROW_BUTTON} tw-text-rose-600 hover:tw-bg-rose-500/10 focus-visible:tw-ring-rose-500/40 dark:tw-text-rose-300`}
                    >
                      {checking ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuTrash2 aria-hidden="true" className="tw-h-4 tw-w-4" />}
                    </button>
                  </div>
                  {error && <p role="alert" className="tw-mt-1 tw-px-1 tw-text-xs tw-font-medium tw-text-rose-600 dark:tw-text-rose-400">{error}</p>}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <footer className="tw-mt-4 tw-flex tw-items-center tw-justify-end tw-gap-2 tw-border-t tw-border-slate-200/70 tw-pt-3 dark:tw-border-white/[0.06]">
        <Button size="sm" onClick={add} disabled={busy}>
          <LuPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
          {text('add')}
        </Button>
        <Button size="sm" variant="primary" onClick={() => save()} disabled={busy} aria-busy={isSaving}>
          {isSaving ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuSave aria-hidden="true" className="tw-h-4 tw-w-4" />}
          {isSaving ? label('card.saving') : label('card.save')}
        </Button>
      </footer>
    </section>
  );
};

ValueListCard.propTypes = {
  list: PropTypes.object.isRequired,
  icon: PropTypes.elementType.isRequired,
  gradient: PropTypes.string.isRequired,
  label: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

export default ValueListCard;
